/* eslint-disable react/prop-types */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    AlertTriangle, CheckCircle2, Clock, History, Info, RefreshCw, RotateCcw,
    ShieldAlert, Smartphone, UserPlus, XCircle,
} from "lucide-react";
import { BIZUM_CONFIG_ANCHOR } from "./BizumReceiverCard.jsx";
import { BrandButton, CopyableField, StatusBadge, TextInput } from "../../../../components/ui/index.js";
import {
    formatAmount, paymentApiError, paymentStatusInfo, refundStatusInfo,
    remainingTimeLabel,
} from "../../../../components/store/paymentStatus.js";

const TONE_ICON = {
    success: <CheckCircle2 className="w-3.5 h-3.5" />,
    error: <XCircle className="w-3.5 h-3.5" />,
    warning: <AlertTriangle className="w-3.5 h-3.5" />,
    info: <Info className="w-3.5 h-3.5" />,
    neutral: <Clock className="w-3.5 h-3.5" />,
};

/** Formularios de acción: cada uno declara sus campos y su endpoint. */
const ACTIONS = {
    confirm: {
        label: "Confirmar pago",
        endpoint: "confirm",
        intent: "success",
        help: "Comprueba código, importe exacto y teléfono antes de confirmar. Un importe distinto abrirá una incidencia.",
        fields: [
            { name: "received_amount", label: "Importe comprobado", type: "number", required: true, step: "0.01" },
            { name: "payer_phone", label: "Teléfono emisor", type: "tel", required: false },
            { name: "notes", label: "Notas internas", type: "textarea", required: false },
        ],
    },
    reject: {
        label: "Rechazar pago",
        endpoint: "reject",
        intent: "error",
        help: "Queda registrado el motivo, tu usuario y la fecha.",
        fields: [
            { name: "reason", label: "Motivo del rechazo", type: "textarea", required: true },
        ],
    },
    reconcile: {
        label: "Conciliar (excepción)",
        endpoint: "reconcile",
        intent: "warning",
        help: "Sólo para un Bizum sin código o con el código mal escrito. Exige teléfono, importe exacto y motivo; queda marcado como excepción auditada.",
        fields: [
            { name: "reason", label: "Motivo de la excepción", type: "textarea", required: true },
            { name: "payer_phone", label: "Teléfono emisor comprobado", type: "tel", required: true },
            { name: "received_amount", label: "Importe comprobado", type: "number", required: true, step: "0.01" },
        ],
    },
    refunds: {
        label: "Registrar devolución",
        endpoint: "refunds",
        intent: "warning",
        help: "No altera el pago original: se guarda como registro aparte con importe, motivo, fecha y responsable.",
        fields: [
            { name: "amount", label: "Importe a devolver", type: "number", required: true, step: "0.01" },
            { name: "reason", label: "Motivo de la devolución", type: "textarea", required: true },
        ],
    },
};

function Field({ field, value, onChange, disabled }) {
    const id = `payment-action-${field.name}`;
    const common = {
        id,
        name: field.name,
        value,
        disabled,
        required: field.required,
        onChange: (event) => onChange(field.name, event.target.value),
    };

    return (
        <div className="form-control gap-1">
            <label className="label py-0" htmlFor={id}>
                <span className="label-text text-xs font-semibold">
                    {field.label}
                    {!field.required && <span className="text-base-content/45"> (opcional)</span>}
                </span>
            </label>
            {field.type === "textarea" ? (
                <textarea {...common} className="textarea textarea-bordered textarea-sm min-h-16" />
            ) : (
                <TextInput
                    {...common}
                    type={field.type}
                    step={field.step}
                    inputSize="sm"
                    inputMode={field.type === "number" ? "decimal" : undefined}
                />
            )}
        </div>
    );
}

/**
 * PaymentReviewPanel — revisión administrativa del pago manual de un pedido.
 *
 * Muestra la evidencia (importes, teléfono, código, receptor), el historial de
 * eventos y las incidencias, y expone las acciones que sólo puede hacer
 * administración. Todas pasan por el backend: aquí no se decide ningún estado.
 */
export default function PaymentReviewPanel({ orderNumber }) {
    const [payment, setPayment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [notIssued, setNotIssued] = useState(false);

    const [openAction, setOpenAction] = useState(null);
    const [form, setForm] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [actionError, setActionError] = useState("");
    const [actionOk, setActionOk] = useState("");
    const [showHistory, setShowHistory] = useState(false);
    const [assignError, setAssignError] = useState("");

    const idempotencyKeyRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        setOpenAction(null);
        try {
            const { data } = await axios.get(`/payments-admin/${orderNumber}/`);
            setPayment(data);
            setNotIssued(false);
        } catch (err) {
            if (err.response?.status === 404) {
                setNotIssued(true);
                setPayment(null);
            } else {
                setLoadError(paymentApiError(err, "No se ha podido cargar el pago."));
            }
        } finally {
            setLoading(false);
        }
    }, [orderNumber]);

    useEffect(() => { load(); }, [load]);

    const openForm = (key) => {
        setOpenAction(key);
        setActionError("");
        setActionOk("");
        idempotencyKeyRef.current =
            globalThis.crypto?.randomUUID?.() ?? `${key}-${Date.now()}-${Math.random()}`;
        // Precarga el importe esperado: lo habitual es que coincida.
        const preset = {};
        for (const field of ACTIONS[key].fields) {
            if (field.name === "received_amount") preset[field.name] = String(payment?.expected_amount ?? "");
            else if (field.name === "amount") preset[field.name] = String(payment?.received_amount ?? "");
            else if (field.name === "payer_phone") preset[field.name] = payment?.payer_phone ?? "";
            else preset[field.name] = "";
        }
        setForm(preset);
    };

    const missingRequired = openAction
        ? ACTIONS[openAction].fields.some(
            (field) => field.required && !String(form[field.name] ?? "").trim(),
        )
        : true;

    const submit = async (event) => {
        event.preventDefault();
        if (submitting || missingRequired) return;   // corta el doble envío

        const action = ACTIONS[openAction];
        setSubmitting(true);
        setActionError("");
        try {
            const payload = { ...form, idempotency_key: idempotencyKeyRef.current };
            for (const key of Object.keys(payload)) {
                if (payload[key] === "") delete payload[key];
            }
            await axios.post(`/payments-admin/${orderNumber}/${action.endpoint}/`, payload);
            setActionOk(`${action.label}: hecho.`);
            idempotencyKeyRef.current = null;
            await load();
        } catch (err) {
            setActionError(paymentApiError(err, "No se ha podido completar la operación."));
        } finally {
            setSubmitting(false);
        }
    };

    const assignReceiver = async () => {
        if (submitting) return;                       // corta el doble clic
        setSubmitting(true);
        setAssignError("");
        setActionError("");
        setActionOk("");
        try {
            const { data } = await axios.post(
                `/payments-admin/${orderNumber}/assign-receiver/`, {},
            );
            const assigned = (data.events || []).find(
                (event) => event.event_type === "receiver_assigned",
            );
            const codeAction = assigned?.payload?.code_action;
            const codeMessage = {
                preserved: "se ha conservado el código que ya tenía",
                issued: "se ha emitido un código nuevo",
                reissued: "el código estaba caducado y se ha emitido otro",
            }[codeAction] ?? "el código no ha cambiado";
            setActionOk(`Receptor asignado: ${codeMessage}.`);
            await load();
        } catch (err) {
            setAssignError(paymentApiError(err, "No se ha podido asignar el receptor."));
        } finally {
            setSubmitting(false);
        }
    };

    const goToReceiverConfig = () => {
        const target = document.getElementById(BIZUM_CONFIG_ANCHOR);
        if (!target) return;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        target.querySelector("button")?.focus();
    };

    const runSimple = async (endpoint, label) => {
        if (submitting) return;
        setSubmitting(true);
        setActionError("");
        setActionOk("");
        try {
            await axios.post(`/payments-admin/${orderNumber}/${endpoint}/`, {});
            setActionOk(`${label}: hecho.`);
            await load();
        } catch (err) {
            setActionError(paymentApiError(err, "No se ha podido completar la operación."));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Carga / error / vacío ────────────────────────────────────────────────
    if (loading) {
        return (
            <section className="rounded-lg bg-base-200/60 p-3 flex flex-col gap-2" aria-busy="true">
                <span className="sr-only">Cargando el pago…</span>
                <div className="skeleton h-4 w-28" />
                <div className="skeleton h-10 w-full" />
            </section>
        );
    }

    if (loadError) {
        return (
            <section role="alert" className="alert alert-error py-2">
                <AlertTriangle className="w-4 h-4" aria-hidden />
                <span className="text-sm">{loadError}</span>
                <button type="button" className="btn btn-xs btn-ghost gap-1" onClick={load}>
                    <RefreshCw className="w-3 h-3" aria-hidden /> Reintentar
                </button>
            </section>
        );
    }

    if (notIssued || !payment) {
        return (
            <section className="rounded-lg bg-base-200/60 p-3 flex flex-col gap-2">
                <h4 className="font-semibold text-sm">Pago manual</h4>
                <p className="text-xs text-base-content/60">
                    Este pedido no tiene pago emitido (es anterior al modelo de cobro).
                </p>
                <button
                    type="button"
                    className="btn btn-xs btn-outline w-fit"
                    disabled={submitting}
                    onClick={async () => {
                        setSubmitting(true);
                        try {
                            await axios.post(`/payments-admin/orders/${orderNumber}/issue/`, {});
                            await load();
                        } catch (err) {
                            setActionError(paymentApiError(err, "No se ha podido emitir el pago."));
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    Emitir pago y código
                </button>
                {actionError && <p className="text-xs text-error">{actionError}</p>}
            </section>
        );
    }

    const info = paymentStatusInfo(payment.status, payment.status_display);
    const remaining = remainingTimeLabel(payment.code_expires_at);
    const amountsDiffer = payment.received_amount != null && payment.amount_matches === false;

    // El backend manda: `has_receiver_snapshot` evita deducirlo de un string.
    const missingReceiver = payment.has_receiver_snapshot === false
        || (payment.has_receiver_snapshot === undefined && !payment.receiver_phone_snapshot);
    const canAssignReceiver = missingReceiver
        && ["pendiente", "informado", "incidencia", "caducado", "rechazado"].includes(payment.status);
    const noActiveReceiver = assignError.includes("receptor Bizum activo")
        || assignError.includes("No hay un receptor");

    const availableActions = Object.entries(ACTIONS).filter(([key]) => {
        if (key === "confirm") return ["pendiente", "informado"].includes(payment.status);
        if (key === "reject") return ["pendiente", "informado", "incidencia"].includes(payment.status);
        if (key === "reconcile") {
            return ["pendiente", "informado", "incidencia", "caducado"].includes(payment.status);
        }
        if (key === "refunds") return payment.status === "confirmado";
        return false;
    });

    // Sin receptor, reemitir un código no arregla nada: el cliente seguiría sin
    // saber a quién pagar. La acción correcta es asignar el receptor.
    const canReissue = !missingReceiver
        && ["pendiente", "informado", "caducado", "rechazado", "incidencia"]
            .includes(payment.status);

    return (
        <section className="rounded-lg bg-base-200/60 p-3 flex flex-col gap-3">
            <header className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="font-semibold text-sm flex items-center gap-1.5">
                    <Smartphone className="w-4 h-4 text-primary" aria-hidden />
                    Pago manual
                </h4>
                <div className="flex items-center gap-1.5">
                    {payment.is_reconciled_exception && (
                        <StatusBadge
                            label="Conciliado"
                            tone="warning"
                            outline
                            icon={<ShieldAlert className="w-3 h-3" />}
                            title="Confirmado por conciliación excepcional"
                        />
                    )}
                    <StatusBadge
                        label={info.label}
                        tone={info.tone}
                        icon={TONE_ICON[info.tone]}
                        title={info.help}
                    />
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-square"
                        onClick={load}
                        aria-label="Actualizar el pago"
                    >
                        <RefreshCw className="w-3 h-3" aria-hidden />
                    </button>
                </div>
            </header>

            {missingReceiver && (
                <div role="alert" className="alert alert-warning py-2">
                    <div className="flex flex-col items-start gap-2 w-full min-w-0">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                            <div className="text-xs">
                                <p className="font-semibold">Receptor no asignado</p>
                                <p>
                                    Este pago se emitió antes de que hubiera un receptor Bizum
                                    configurado, así que el cliente no ve a quién pagar.
                                </p>
                            </div>
                        </div>

                        {assignError && (
                            <p className="text-xs text-error">{assignError}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-2">
                        {canAssignReceiver ? (
                            <button
                                type="button"
                                className="btn btn-xs btn-warning gap-1"
                                disabled={submitting}
                                onClick={assignReceiver}
                            >
                                {submitting
                                    ? <span className="loading loading-spinner loading-xs" aria-hidden />
                                    : <UserPlus className="w-3 h-3" aria-hidden />}
                                Asignar receptor activo
                            </button>
                        ) : (
                            <span className="text-xs text-base-content/60">
                                Este pago ya está cerrado y no admite la reparación.
                            </span>
                        )}
                        {noActiveReceiver && (
                                <button
                                    type="button"
                                    className="btn btn-xs btn-outline"
                                    onClick={goToReceiverConfig}
                                >
                                    Ir a la configuración de cobro
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {amountsDiffer && (
                <div role="alert" className="alert alert-error py-2">
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    <span className="text-xs">{payment.incident_reason}</span>
                </div>
            )}

            {/* Evidencia */}
            <div className="grid grid-cols-2 gap-3 text-sm">
                <CopyableField
                    label="Esperado"
                    value={String(payment.expected_amount)}
                    display={formatAmount(payment.expected_amount, payment.currency)}
                    mono={false}
                />
                <CopyableField
                    label="Recibido"
                    value={payment.received_amount != null ? String(payment.received_amount) : ""}
                    display={
                        payment.received_amount != null
                            ? formatAmount(payment.received_amount, payment.currency)
                            : undefined
                    }
                    mono={false}
                    emptyText="Sin comprobar"
                />
                <CopyableField
                    label="Código activo"
                    value={payment.payment_code}
                    emptyText="Sin código"
                    helpText={
                        payment.code_expires_at
                            ? (remaining ? `Quedan ${remaining}` : "Caducado")
                            : undefined
                    }
                />
                <CopyableField
                    label="Teléfono emisor"
                    value={payment.payer_phone}
                    mono={false}
                    emptyText="No comunicado"
                    helpText={
                        payment.reported_at
                            ? `Comunicado el ${new Date(payment.reported_at).toLocaleDateString("es-ES")}`
                            : undefined
                    }
                />
            </div>

            {payment.rejection_reason && (
                <p className="text-xs text-base-content/70">
                    <span className="font-semibold">Motivo del rechazo: </span>
                    {payment.rejection_reason}
                </p>
            )}
            {payment.reconciliation_reason && (
                <p className="text-xs text-base-content/70">
                    <span className="font-semibold">Motivo de la conciliación: </span>
                    {payment.reconciliation_reason}
                </p>
            )}

            {actionOk && (
                <div role="status" className="alert alert-success py-2">
                    <CheckCircle2 className="w-4 h-4" aria-hidden />
                    <span className="text-xs">{actionOk}</span>
                </div>
            )}
            {actionError && !openAction && (
                <div role="alert" className="alert alert-error py-2">
                    <AlertTriangle className="w-4 h-4" aria-hidden />
                    <span className="text-xs">{actionError}</span>
                </div>
            )}

            {/* Acciones */}
            {availableActions.length === 0 && !canReissue ? (
                <p className="text-xs text-base-content/55">
                    Este pago está cerrado y no admite más acciones.
                </p>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {availableActions.map(([key, action]) => (
                        <button
                            key={key}
                            type="button"
                            className={`btn btn-xs ${openAction === key ? "btn-active" : "btn-outline"}`}
                            disabled={submitting}
                            aria-expanded={openAction === key}
                            onClick={() => (openAction === key ? setOpenAction(null) : openForm(key))}
                        >
                            {action.label}
                        </button>
                    ))}
                    {canReissue && (
                        <button
                            type="button"
                            className="btn btn-xs btn-outline gap-1"
                            disabled={submitting}
                            onClick={() => runSimple("reissue-code", "Código reemitido")}
                        >
                            <RotateCcw className="w-3 h-3" aria-hidden />
                            Reemitir código
                        </button>
                    )}
                </div>
            )}

            {openAction && (
                <form onSubmit={submit} className="flex flex-col gap-2 border-t border-base-content/10 pt-2">
                    <p className="text-xs text-base-content/60">{ACTIONS[openAction].help}</p>
                    {ACTIONS[openAction].fields.map((field) => (
                        <Field
                            key={field.name}
                            field={field}
                            value={form[field.name] ?? ""}
                            disabled={submitting}
                            onChange={(name, value) => setForm((cur) => ({ ...cur, [name]: value }))}
                        />
                    ))}
                    {actionError && (
                        <div role="alert" className="alert alert-error py-2">
                            <AlertTriangle className="w-4 h-4" aria-hidden />
                            <span className="text-xs">{actionError}</span>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <BrandButton type="submit" size="sm" disabled={submitting || missingRequired}>
                            {submitting && <span className="loading loading-spinner loading-xs" aria-hidden />}
                            {submitting ? "Guardando…" : ACTIONS[openAction].label}
                        </BrandButton>
                        <button
                            type="button"
                            className="btn btn-xs btn-ghost"
                            disabled={submitting}
                            onClick={() => setOpenAction(null)}
                        >
                            Cancelar
                        </button>
                    </div>
                </form>
            )}

            {/* Devoluciones */}
            {(payment.refunds || []).length > 0 && (
                <div className="border-t border-base-content/10 pt-2 flex flex-col gap-1.5">
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-base-content/55">
                        Devoluciones
                    </h5>
                    {payment.refunds.map((refund) => {
                        const rInfo = refundStatusInfo(refund.status, refund.status_display);
                        return (
                            <div key={refund.id} className="flex items-center justify-between gap-2 text-xs">
                                <span className="min-w-0 truncate">
                                    {formatAmount(refund.amount, refund.currency)} · {refund.reason}
                                </span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <StatusBadge label={rInfo.label} tone={rInfo.tone} size="xs" />
                                    {refund.status === "devolucion_pendiente" && (
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-outline"
                                            disabled={submitting}
                                            onClick={async () => {
                                                setSubmitting(true);
                                                try {
                                                    await axios.post(`/payments-admin/refunds/${refund.id}/complete/`, {});
                                                    await load();
                                                } catch (err) {
                                                    setActionError(paymentApiError(err, "No se ha podido completar."));
                                                } finally {
                                                    setSubmitting(false);
                                                }
                                            }}
                                        >
                                            Marcar devuelto
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Historial */}
            <div className="border-t border-base-content/10 pt-2">
                <button
                    type="button"
                    className="btn btn-xs btn-ghost gap-1"
                    aria-expanded={showHistory}
                    onClick={() => setShowHistory((v) => !v)}
                >
                    <History className="w-3 h-3" aria-hidden />
                    {showHistory ? "Ocultar historial" : `Historial (${(payment.events || []).length})`}
                </button>
                {showHistory && (
                    <ul className="mt-2 flex flex-col gap-1.5 max-h-56 overflow-y-auto scrollbar_mod">
                        {(payment.events || []).map((event) => (
                            <li key={event.id} className="text-xs border-l-2 border-base-content/20 pl-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold">{event.event_type_display}</span>
                                    <span className="text-base-content/50">
                                        {new Date(event.created_at).toLocaleString("es-ES")}
                                    </span>
                                </div>
                                <div className="text-base-content/60">
                                    {event.actor_email || "sistema"}
                                    {event.from_status && event.to_status && event.from_status !== event.to_status
                                        ? ` · ${event.from_status} → ${event.to_status}`
                                        : ""}
                                </div>
                                {event.reason && (
                                    <div className="text-base-content/60 italic">{event.reason}</div>
                                )}
                            </li>
                        ))}
                        {(payment.events || []).length === 0 && (
                            <li className="text-xs text-base-content/50">Sin eventos registrados.</li>
                        )}
                    </ul>
                )}
            </div>
        </section>
    );
}
